/**
 * Teacher hub at /teachers/dashboard with three big cards.
 * Auth-gated via TeacherAuthContext.
 */
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTeacherAuth } from "../TeacherAuthContext";

const CARDS = [
  {
    to: "/teachers/lessons",
    title: "My lessons",
    desc: "See your upcoming lessons and walk into the classroom when it's time.",
    testid: "teacher-card-lessons",
  },
  {
    to: "/teachers/profile",
    title: "My profile",
    desc: "Update your name, bio, photo, and password.",
    testid: "teacher-card-profile",
  },
  {
    to: "/teachers/resources",
    title: "Resources",
    desc: "Materials and references shared by the school.",
    testid: "teacher-card-resources",
  },
];

export default function TeacherDashboard() {
  const { status, teacher, logout, signingOutRef } = useTeacherAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "anon" && !signingOutRef.current) navigate("/teachers", { replace: true });
  }, [status, navigate, signingOutRef]);

  // Enforce password rotation: temp-password teachers can't use the dashboard.
  useEffect(() => {
    if (status === "auth" && teacher?.must_change_password) {
      navigate("/teachers/profile", { replace: true });
    }
  }, [status, teacher, navigate]);

  if (status !== "auth") return null;
  if (teacher?.must_change_password) return null;

  function signOut() {
    signingOutRef.current = true;
    logout();
    navigate("/", { replace: true });
  }

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-20 md:pt-24 pb-24 px-6 md:px-10" data-testid="teacher-dashboard-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
              Teacher
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              {teacher?.name ? `Hola, ${teacher.name}.` : "Hola."}
            </h1>
            <p className="mt-2 text-sm text-[#1C1C1C]/55">{teacher?.email}</p>
          </div>
          <button
            onClick={signOut}
            data-testid="teacher-signout-btn"
            className="px-4 py-2 text-[#1C1C1C]/60 text-xs tracking-[0.18em] uppercase font-medium hover:text-[#1C1C1C] transition-colors"
          >
            Sign out
          </button>
        </div>

        {teacher?.must_change_password && (
          <div className="mb-8 px-5 py-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-900" data-testid="teacher-pw-warning">
            You're using a temporary password. Please{" "}
            <Link to="/teachers/profile" className="underline font-medium">change it from your profile</Link>.
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {CARDS.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className="group block bg-white border border-[#1C1C1C]/10 rounded-2xl md:rounded-3xl p-7 md:p-8 hover:border-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-[#FAF9F6] transition-all"
            >
              <div className="text-[10px] tracking-[0.32em] uppercase font-medium opacity-55">→</div>
              <div className="mt-4 font-display text-2xl md:text-3xl tracking-tight">{c.title}</div>
              <p className="mt-3 text-sm md:text-base opacity-70 leading-relaxed">{c.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
